import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, filter, map } from 'rxjs';

import { Logger } from '@camelot/server';
import { isNonNullable } from '@camelot/utils';

import { Permissions } from './permissions';

export type PermissionVerb = 'create' | 'read' | 'update' | 'delete' | 'manage' | 'execute';

export type Level = 'authenticated' | 'authorize' | 'reader' | 'contributor' | 'administrator' | PermissionVerb;

export type PermissionFeature = 'chift' | 'ticketing' | 'financial';

export type Domain =
  | 'automation'
  | 'invoice'
  | 'invoice-purchase'
  | 'payment'
  | 'contact'
  | 'task'
  | 'project'
  | 'quotation'
  | 'maintenance'
  | 'conversation'
  | 'user'
  | 'document'
  | 'organization'
  | 'article'
  | 'article-sale-price'
  | 'article-purchase-price'
  | 'composition'
  | 'dashboard'
  | 'setting'
  | 'validation';

export type Capability = 'task-reopen' | 'task-email' | 'quotation-email' | 'invoice-email' | 'invoice-cancel';

export type Permission = `${PermissionVerb}:${Domain}` | `execute:${Capability}`;

export interface PermissionsInfo {
  permissions: string[];
  roles: string[];
  features: PermissionFeature[];
}

const accessLevels = ['reader', 'contributor', 'administrator'];

// Verbs each role level grants on its domain, strictly.
const verbsByLevel: { [index: string]: PermissionVerb[] } = {
  administrator: ['create', 'read', 'update', 'delete', 'manage', 'execute'],
  contributor: ['create', 'read', 'update'],
  reader: ['read'],
};

@Injectable({
  providedIn: 'root',
})
export class CamPermissionsService {
  private _updated$ = new BehaviorSubject<number | null>(null);
  private _isFill = { isAuthenticated: false, permissions: false };
  private _granted = new Map<string, Set<string>>();
  private _backendFeatures: (string | PermissionFeature)[] = [];

  public bypassed = false;
  public mockFeatures: string[] | null = null;
  public mockPermissions: string[] | null = null;
  public features: (string | PermissionFeature)[] = [];
  public guards: { [index: string]: Level } = {};
  public permissions: string[] = [];
  public roles: string[] = [];
  public isAuthenticated: boolean = false;

  public updated$ = this._updated$.pipe(filter(isNonNullable));

  get received() {
    return this._updated$.value !== null;
  }

  // Dev-only cheat: every permission is granted; tenant features stay as received. The caller checks the environment.
  public setBypass(enabled: boolean) {
    this.bypassed = enabled;
    if (this.received) {
      this._updated$.next(Date.now());
    }
  }

  // Dev-only cheat: replaces the backend permissions until reset with null; the caller checks the environment.
  public setMockPermissions(permissions: string[] | null) {
    this.mockPermissions = permissions;
    this._granted = parsePermissions(permissions ?? this.permissions);
    if (this.received) {
      this._updated$.next(Date.now());
    }
  }

  // Dev-only cheat: replaces the backend features until reset with null; the caller checks the environment.
  public setMockFeatures(features: string[] | null) {
    this.mockFeatures = features;
    this.features = features ?? this._backendFeatures;
    if (this.received) {
      this._updated$.next(Date.now());
    }
  }

  public set(info: PermissionsInfo, isAuthenticated: boolean) {
    Logger.LogInfo('[PERMISSIONS] List brut:', info.permissions);

    this._backendFeatures = info.features || [];
    this.features = this.mockFeatures ?? this._backendFeatures;
    this.roles = info.roles || [];
    this.permissions = info.permissions || [];
    this._granted = parsePermissions(this.mockPermissions ?? this.permissions);

    this.guards = this.roles
      .map(role => role.replace(/^merlin_/i, '').toLowerCase())
      .reduce<typeof this.guards>((acc, role) => {
        if (!role.includes('-')) {
          return acc;
        }
        const [domain, access] = role.split('-');

        const lastAccess =
          accessLevels.indexOf(access) > accessLevels.indexOf(acc[domain] || '') ? access : acc[domain];
        return {
          ...acc,
          [domain]: <Level>lastAccess,
        };
      }, {});
    Logger.LogInfo('[PERMISSIONS] Guard', this.guards);

    this._isFill.permissions = true;

    this.setSilentAuthenticated(isAuthenticated);
    // Tristan still reads the deprecated singleton directly.
    Permissions.set(info, isAuthenticated);

    this._canYouUpdate();
  }

  public setSilentAuthenticated(isAuthenticated: boolean) {
    this.isAuthenticated = isAuthenticated;
    this._isFill.isAuthenticated = true;
    Permissions.setSilentAuthenticated(isAuthenticated);

    this._canYouUpdate();
  }
  public setAuthenticated(isAuthenticated: boolean) {
    this.isAuthenticated = isAuthenticated;

    Permissions.setAuthenticated(isAuthenticated);

    this._updated$.next(Date.now());
  }

  public hasRole(role: string): boolean {
    return this.roles.some(x => x === role);
  }

  public canDirectAccess(feature: PermissionFeature | Domain | string, level: Level | string): boolean {
    if (level === 'authenticated') {
      return this.isAuthenticated;
    }

    if (level === 'authorize') {
      return this.features.includes(feature);
    }

    if (this.bypassed) {
      return true;
    }

    if (!feature) {
      return Permissions.canDirectAccess(feature, level);
    }

    const featureLevel = this._levelOf(feature);

    if (!accessLevels.includes(level)) {
      return this._isGranted(feature, level) || (verbsByLevel[featureLevel] ?? []).includes(<PermissionVerb>level);
    }

    return accessLevels.indexOf(featureLevel) >= accessLevels.indexOf(level);
  }

  public canAccess$(feature: string | PermissionFeature, level: Level | string): Observable<boolean> {
    return this._updated$.pipe(map(() => this.canDirectAccess(feature, level)));
  }

  // The claim lists `verb:resource` entries; `administrator:<resource>` is the joker, handled by the ladder.
  private _isGranted(resource: string, verb: string): boolean {
    return !!this._granted.get(resource)?.has(verb);
  }

  // Highest ladder level on the resource, from a role or a `<level>:<resource>` entry; a mock silences the roles.
  private _levelOf(resource: string): string {
    const fromRole = this.mockPermissions === null ? this.guards[resource] : undefined;
    const candidates = [fromRole, ...(this._granted.get(resource) ?? [])].filter(
      (candidate): candidate is string => !!candidate && accessLevels.includes(candidate)
    );
    return candidates.sort((a, b) => accessLevels.indexOf(b) - accessLevels.indexOf(a))[0] ?? '';
  }

  private _canYouUpdate() {
    if (!this._isFill.isAuthenticated || !this._isFill.permissions) {
      return;
    }
    this._updated$.next(Date.now());
  }
}

export function parsePermissions(permissions: string[]): Map<string, Set<string>> {
  const granted = new Map<string, Set<string>>();
  for (const permission of permissions) {
    const [verb, resource] = permission.split(':');
    if (!verb || !resource) {
      continue;
    }
    granted.set(resource, (granted.get(resource) ?? new Set<string>()).add(verb));
  }
  return granted;
}
